import React, { useState } from 'react';
import { Modal, Button, message } from 'antd';
import { WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { unassignStudies } from '../../view/studies/StudiesService';
import { writeNotification } from '../../utils/notificationStore';

const warningBoxStyle = {
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  fontSize: 14,
};
const dangerBoxStyle = {
  background: '#fef2f2',
  border: '1px solid #fca5a5',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  fontSize: 14,
};
const draftBoxStyle = {
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  fontSize: 14,
};
const infoBoxStyle = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
};
const infoTitleStyle = { fontWeight: 600, fontSize: 13, marginBottom: 8 };
const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 };
const labelStyle = { color: '#6b7280' };

export function UnassignStudyDialog({ study, open, onOpenChange, onUnassign, reportStatus, assigneeId }) {
  const [loading, setLoading] = useState(false);
  // For submitted studies, require an explicit second confirmation step
  const [submittedConfirmed, setSubmittedConfirmed] = useState(false);

  const handleClose = () => {
    setSubmittedConfirmed(false);
    onOpenChange(false);
  };

  const handleUnassign = async () => {
    if (!study) return;
    setLoading(true);
    try {
      const res = await unassignStudies([study.studyInstanceUID]);

      // HTTP 200 does NOT mean this study was unassigned — the endpoint reports
      // per-study results, and refuses studies it must not silently withdraw
      // (a teleradiology case already claimed by a remote radiologist, or one
      // whose report has been delivered). Reporting success regardless is what
      // made "unassigned successfully" appear on studies that never moved.
      const payload = res?.data?.data || {};
      const failed = Array.isArray(payload.failed) ? payload.failed : [];
      if (failed.includes(study.studyInstanceUID)) {
        const reason = payload.reasons?.[study.studyInstanceUID];
        message.error(reason ? `Could not unassign: ${reason}` : 'Failed to unassign study');
        setLoading(false);
        return;
      }

      // Notify the radiologist who is being unassigned so their WorklistPage
      // shows an informative alert rather than silently removing the study.
      if (assigneeId && study.studyInstanceUID) {
        writeNotification(assigneeId, {
          id: `unassign-${study.studyInstanceUID}-${Date.now()}`,
          type: 'unassigned',
          studyInstanceUID: study.studyInstanceUID,
          patientName: study.patientName || 'Unknown Patient',
          bodyPart: study.bodyPart || '',
          modality: study.modality || '',
          studyDate: study.studyDate || '',
        });
      }

      message.success('Study unassigned successfully');
      setSubmittedConfirmed(false);
      onUnassign?.(study);
      onOpenChange(false);
    } catch (err) {
      message.error('Failed to unassign study');
    } finally {
      setLoading(false);
    }
  };

  if (!study) return null;

  const isSubmitted = reportStatus === 'submitted';
  const isDraft = reportStatus === 'draft';

  // Build footer buttons based on report status
  const footerButtons = [];
  footerButtons.push(
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>
  );

  if (isSubmitted && !submittedConfirmed) {
    footerButtons.push(
      <Button
        key="confirm-override"
        danger
        onClick={() => setSubmittedConfirmed(true)}
      >
        I understand — Show Override Option
      </Button>
    );
  } else {
    footerButtons.push(
      <Button
        key="unassign"
        type="primary"
        danger={isSubmitted}
        style={!isSubmitted ? { background: '#ea580c', borderColor: '#ea580c' } : undefined}
        loading={loading}
        onClick={handleUnassign}
      >
        {isSubmitted ? 'Force Unassign (Override Submitted Report)' : 'Unassign Study'}
      </Button>
    );
  }

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WarningOutlined style={{ color: isSubmitted ? '#dc2626' : '#f97316', fontSize: 18 }} />
          Unassign Study
        </span>
      }
      visible={open}
      onCancel={handleClose}
      width={500}
      footer={footerButtons}
    >
      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        This will remove the radiologist assignment from this study.
      </p>

      {/* Report status warnings */}
      {isSubmitted && (
        <div style={dangerBoxStyle}>
          <p style={{ margin: 0, color: '#7f1d1d', fontWeight: 600 }}>
            <ExclamationCircleOutlined style={{ marginRight: 6 }} />
            A report has already been submitted for this study.
          </p>
          <p style={{ margin: '8px 0 0', color: '#991b1b', fontSize: 13 }}>
            Unassigning will not delete the submitted report, but the radiologist will no longer see this study in their worklist. Only do this if there is a legitimate reason to override.
          </p>
        </div>
      )}
      {isDraft && !isSubmitted && (
        <div style={draftBoxStyle}>
          <p style={{ margin: 0, color: '#92400e' }}>
            <WarningOutlined style={{ marginRight: 6 }} />
            The radiologist has a <strong>draft report in progress</strong> for this study.
            Unassigning will remove it from their worklist — their draft will be preserved but inaccessible to them.
          </p>
        </div>
      )}

      {/* Standard assignment warning */}
      {!isSubmitted && (
        <div style={warningBoxStyle}>
          <p style={{ margin: 0, color: '#111827' }}>
            This study is currently assigned to{' '}
            <strong>{study.assignedRadiologist || study.referringPhysician || 'a radiologist'}</strong>.
            Unassigning will remove it from their worklist.
          </p>
        </div>
      )}

      {/* Study Details */}
      <div style={infoBoxStyle}>
        <div style={infoTitleStyle}>Study Details</div>
        <div style={gridStyle}>
          <div><span style={labelStyle}>Patient:</span> {study.patientName}</div>
          <div><span style={labelStyle}>Patient ID:</span> {study.patientID}</div>
          <div><span style={labelStyle}>Modality:</span> {study.modality}</div>
          <div><span style={labelStyle}>Date:</span> {study.studyDate}</div>
          {reportStatus && reportStatus !== 'none' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={labelStyle}>Report Status:</span>{' '}
              <strong style={{ color: isSubmitted ? '#dc2626' : isDraft ? '#d97706' : 'inherit' }}>
                {isSubmitted ? 'Submitted' : isDraft ? 'Draft in Progress' : reportStatus}
              </strong>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
